import type { EntryPoint, FlowState } from './Flow.types';
import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { useCarStore } from '@/entities/Car';
import { usePolicyStore } from '@/entities/Policy';
import { useQuoteStore } from '@/entities/Quote';
import { useUserStore } from '@/entities/User';
import { useFormHandlerService } from '@/shared/api';
import { useStateUpdate } from '@/shared/lib/pinia/useStateUpdate';
import { clearQueryParam, normalizePath } from '@/shared/utils/vue-router';

const storeKey = 'FlowStore';

function defaultState(): FlowState {
  return {
    isUpgradeTplComprehensiveFlow: false,
    isExceptionPage: false,
    isResultPage: false,
    entryPoint: null,
    campaignOffer: '',
    isCarDetailsFullfilled: false,
    isVinSkipped: false,
    isContinueBuying: false,
    upgradeInstalmentPayment: false,
  };
}

export const useFlowStore = defineStore(storeKey, () => {
  const state = reactive<FlowState>(defaultState());
  const $update = useStateUpdate<FlowState>(state);

  const route = useRoute();
  const policyStore = usePolicyStore();
  const userStore = useUserStore();

  function checkUpgradeTplComprehensiveFlow(offer?: string) {
    const upgradeOfferKey = 'upgrade-tpl-comprehensive';
    const isUpgradeTplComprehensiveFlowCheckedByParams
      = route.query.offer === upgradeOfferKey || offer === upgradeOfferKey;
    state.isUpgradeTplComprehensiveFlow = isUpgradeTplComprehensiveFlowCheckedByParams;

    if (route.query.policy && typeof route.query.policy === 'string') {
      policyStore.state.policyId = route.query.policy;
    }
    if (route.query.qid && typeof route.query.qid === 'string') {
      userStore.state.userId = route.query.qid;
    }
  }

  async function checkCachedPhoneAndSaveItIfFound() {
    if (!route.path.endsWith('/phone')) return;

    const LS_PHONE_KEY = 'car-insurance_phone';
    const phone = localStorage.getItem(LS_PHONE_KEY);
    if (!phone) return false;
    userStore.state.phone = phone.replace(/[\s+]/g, '');
    await useFormHandlerService().phone({ phone: userStore.state.phone });
    localStorage.removeItem(LS_PHONE_KEY);
    return true;
  }

  const quoteStore = useQuoteStore();
  function checkAndSetPromoCode() {
    if (route.query.promo && typeof route.query.promo === 'string') {
      quoteStore.state.promoCode = route.query.promo;
    }
  }

  async function checkEntryPointAndClearIt() {
    const normalizedPath = normalizePath(route.path);
    if (route.query.entrypoint) {
      state.entryPoint = route.query.entrypoint as EntryPoint;
      await clearQueryParam(normalizedPath, 'entrypoint');
    }
  }

  function checkAndSetCampaignOffer() {
    if (route.query.offer && typeof route.query.offer === 'string') {
      state.campaignOffer = route.query.offer;
    }
  }

  const carStore = useCarStore();
  const isCommercial = computed(() => {
    // TODO: change codes according Confluence;
    const COMMERCIAL_USAGE_CODES = new Set(['1002']);
    return COMMERCIAL_USAGE_CODES.has(carStore.state.vehicleUsage.id);
  });

  return {
    state,
    $update,
    checkUpgradeTplComprehensiveFlow,
    checkCachedPhoneAndSaveItIfFound,
    checkAndSetPromoCode,
    checkEntryPointAndClearIt,
    checkAndSetCampaignOffer,
    isCommercial,
  };
}, { persist: { key: storeKey } });
